import { useEffect, useLayoutEffect, useRef } from 'react';
import { haptic } from './theme.jsx';
import {
  createVelocityTracker, rubberband, project, durationFor, tween, prefersReducedMotion,
} from './hooks/gesture/motion.js';
import { emitSwipeProgress } from './hooks/gesture/swipe-progress.js';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

// Horizontales Wischen zwischen Tabs — "follow the finger":
//  • 1:1-Tracking per rAF direkt auf transform (kein React-State pro Move)
//  • Achsen-Lock nach ~10px, horizontale Scroller (Chips) und linker Rand (Swipe-Back) ausgenommen
//  • Rubber-Band am ersten/letzten Tab, Release velocity-basiert (Momentum-Projektion)
//  • Tabwechsel räumlich konsistent: alter Tab fliegt zur Wischrichtung raus, neuer kommt von
//    der Gegenseite (auch bei Tap auf die Bottom-Nav), unterbrechbar.
// Touch-Events statt Pointer-Events: in den Tabs liegen vertikale Scroll-Container; Pointer-
// Events würden dort beim ersten Move ein pointercancel liefern.
// Fortschritt geht pro Frame über den swipe-progress-Bus an die Bottom-Nav; onSwipeProgress
// (Container-State) wird nur noch beim Zurücksetzen (0) aufgerufen.
export default function SwipeableTabs({
  tabs,
  activeTab,
  onChange,
  onSwipeProgress,
  children,
  disabled = false,
}) {
  const wrapperRef = useRef(null);
  const paneRef = useRef(null);
  const anim = useRef(null);
  const x = useRef(0);
  const raf = useRef(0);
  const pendingEnter = useRef(null); // Startversatz des neuen Tabs nach Wisch-Commit
  const lastIndex = useRef(Math.max(0, tabs.findIndex(t => t.id === activeTab)));

  const paint = () => {
    raf.current = 0;
    const el = paneRef.current;
    if (!el) return;
    const v = x.current;
    // In Ruhe transform:none — sonst wird ein Containing-Block für position:fixed-Kinder erzeugt
    el.style.transform = v ? `translate3d(${v}px, 0, 0)` : 'none';
    el.style.opacity = v ? String(1 - Math.min(0.35, Math.abs(v) / window.innerWidth * 0.5)) : '';
  };
  const schedule = () => { if (!raf.current) raf.current = requestAnimationFrame(paint); };
  const setX = (v) => { x.current = v; schedule(); };
  const settle = () => {
    const el = paneRef.current;
    if (el) el.style.willChange = '';
    x.current = 0;
    paint();
  };

  useEffect(() => {
    if (disabled) {
      anim.current?.stop();
      anim.current = null;
      settle();
      emitSwipeProgress(0);
      onSwipeProgress?.(0);
    }
  }, [disabled, onSwipeProgress]);

  // Tabwechsel (Wisch-Commit oder Tap): neuen Tab von der passenden Seite hereinholen
  useLayoutEffect(() => {
    const idx = Math.max(0, tabs.findIndex(t => t.id === activeTab));
    const prev = lastIndex.current;
    lastIndex.current = idx;
    if (idx === prev) return;
    const el = paneRef.current;
    if (!el || prefersReducedMotion()) { pendingEnter.current = null; settle(); return; }
    const w = window.innerWidth;
    const from = pendingEnter.current ?? (idx > prev ? w * 0.18 : -w * 0.18);
    pendingEnter.current = null;
    anim.current?.stop();
    el.style.willChange = 'transform';
    x.current = from;
    paint();
    anim.current = tween({
      from, to: 0, duration: 300,
      onUpdate: (v) => { x.current = v; paint(); },
      onDone: () => { anim.current = null; settle(); },
    });
  }, [activeTab, tabs]);

  useEffect(() => {
    const pane = paneRef.current;
    if (!pane || typeof window === 'undefined') return undefined;
    const target = pane;
    const g = { active: false, decided: false, horizontal: false, startX: 0, startY: 0, base: 0 };
    const tracker = createVelocityTracker();
    const isTouchOnly = () => window.matchMedia('(hover: none)').matches;
    const index = () => Math.max(0, tabs.findIndex((tab) => tab.id === activeTab));

    const inHorizontalScroller = (el) => {
      for (let n = el; n && n !== pane; n = n.parentElement) {
        const ox = getComputedStyle(n).overflowX;
        if ((ox === 'auto' || ox === 'scroll') && n.scrollWidth > n.clientWidth + 1) return true;
      }
      return false;
    };

    const onStart = (event) => {
      if (disabled || !isTouchOnly() || tabs.length < 2) return;
      const t = event.touches?.[0];
      if (!t || event.touches.length > 1) return;
      if (t.clientX <= 20) return; // linker Rand gehört der Swipe-Back-Geste
      if (inHorizontalScroller(event.target)) return;
      // Laufende Animation greifen → vom sichtbaren Wert weiter
      let base = 0;
      if (anim.current) { base = anim.current.stop(); anim.current = null; }
      Object.assign(g, { active: true, decided: base !== 0, horizontal: base !== 0, startX: t.clientX, startY: t.clientY, base });
      tracker.reset();
      tracker.add(base);
    };

    const onMove = (event) => {
      if (!g.active || disabled) return;
      const t = event.touches?.[0];
      if (!t) return;
      const dx = t.clientX - g.startX;
      const dy = t.clientY - g.startY;
      if (!g.decided) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        g.decided = true;
        g.horizontal = Math.abs(dx) > Math.abs(dy) * 1.2;
        if (!g.horizontal) { g.active = false; return; }
        pane.style.willChange = 'transform';
      }
      if (!g.horizontal) return;
      event.preventDefault();
      const w = window.innerWidth;
      const raw = g.base + dx;
      const i = index();
      const atEdge = (i === 0 && raw > 0) || (i === tabs.length - 1 && raw < 0);
      setX(atEdge ? rubberband(raw, w) : raw);
      tracker.add(raw);
      emitSwipeProgress(clamp(-x.current / w, -1, 1));
    };

    const onEnd = (event) => {
      if (!g.active) return;
      g.active = false;
      if (!g.horizontal) return;
      const w = window.innerWidth;
      const v = event?.type === 'touchcancel' ? 0 : tracker.velocity();
      const i = index();
      const projected = x.current + project(v, 0.995);
      let target = i;
      if (projected < -w * 0.35 && v <= 0.05) target = Math.min(tabs.length - 1, i + 1);
      else if (projected > w * 0.35 && v >= -0.05) target = Math.max(0, i - 1);
      const dir = target > i ? -1 : target < i ? 1 : 0;
      const to = dir === 0 ? 0 : dir * w;
      const reduced = prefersReducedMotion();
      anim.current = tween({
        from: x.current, to,
        duration: reduced ? 0 : durationFor(to - x.current, v, { min: 160, max: 340 }),
        onUpdate: (val) => { x.current = val; paint(); emitSwipeProgress(clamp(-val / w, -1, 1)); },
        onDone: () => {
          anim.current = null;
          // commit: Nav-Kapsel springt nicht zurück, sondern übernimmt die neue Position direkt
          emitSwipeProgress(0, { commit: dir !== 0 });
          onSwipeProgress?.(0);
          if (dir !== 0) {
            haptic(6);
            pendingEnter.current = -dir * w * 0.3; // neuer Tab kommt von der Gegenseite
            onChange?.(tabs[target]?.id);
          } else {
            settle();
          }
        },
      });
    };

    target.addEventListener('touchstart', onStart, { passive: true });
    target.addEventListener('touchmove', onMove, { passive: false });
    target.addEventListener('touchend', onEnd, { passive: true });
    target.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      target.removeEventListener('touchstart', onStart);
      target.removeEventListener('touchmove', onMove);
      target.removeEventListener('touchend', onEnd);
      target.removeEventListener('touchcancel', onEnd);
    };
  }, [activeTab, disabled, onChange, onSwipeProgress, tabs]);

  useEffect(() => () => {
    anim.current?.stop();
    if (raf.current) cancelAnimationFrame(raf.current);
  }, []);

  return (
    <div ref={wrapperRef} style={{ minHeight: 0, display: 'contents' }}>
      <div
        ref={paneRef}
        style={{
          height: '100%',
          transform: 'none',
          minHeight: 0,
        }}
      >
        {children?.[Math.max(0, tabs.findIndex(t => t.id === activeTab))] ?? null}
      </div>
    </div>
  );
}
